import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { api } from '../lib/api';

/**
 * BugBot chat widget (Dan, 2026-09-21) -- floating launcher + panel,
 * mounted once in App.jsx so it's available on every route for both csr
 * and admin. Proxies through this app's OWN POST /api/bugbot/chat --
 * never talks to BugBot's backend directly from the browser, so the
 * BugBot API key never ships in this bundle. See backend/src/lib/bugbot.js
 * and docs/BUGBOT_INTEGRATION.md.
 *
 * v1 is read-only Q&A (Dan, 2026-09-21) -- BugBot has no CSR-specific
 * write tools yet (docs/OPEN_ITEMS.md #8). If a query DOES hit one of
 * BugBot's existing write tools, its confirmation prompt ("Reply YES to
 * execute...") just comes back as a normal markdown reply -- nothing
 * special needed here, the user's next message (e.g. "YES") goes through
 * like any other chat turn.
 */
export default function BugBotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  async function send(e) {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    setInput('');
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setSending(true);
    try {
      const { reply } = await api.post('/bugbot/chat', { message: text });
      setMessages((prev) => [...prev, { role: 'bot', text: reply || "(BugBot didn't say anything back)" }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="bugbot-widget">
      {open && (
        <div className="bugbot-panel">
          <div className="bugbot-panel-header">
            <span className="bugbot-panel-title">
              <img src="/bugbot-icon-32.png" alt="" width="20" height="20" />
              <strong>BugBot</strong>
            </span>
            <button type="button" className="bugbot-panel-close" onClick={() => setOpen(false)} aria-label="Close BugBot chat">
              ×
            </button>
          </div>

          <div className="bugbot-messages" ref={listRef}>
            {messages.length === 0 && (
              <p className="hint-text" style={{ margin: '8px 4px' }}>
                Ask BugBot about a customer, a job, or anything else it already knows.
              </p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`bugbot-message bugbot-message-${m.role}`}>
                <ReactMarkdown components={{ a: (props) => <a {...props} target="_blank" rel="noreferrer" /> }}>
                  {m.text}
                </ReactMarkdown>
              </div>
            ))}
            {sending && <div className="bugbot-message bugbot-message-bot bugbot-typing">Thinking…</div>}
          </div>

          {error && <div className="error-banner" style={{ margin: '0 12px 8px' }}>{error}</div>}

          <form onSubmit={send} className="bugbot-input-row">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask BugBot…"
              disabled={sending}
              style={{ marginBottom: 0 }}
            />
            <button type="submit" className="btn btn-primary" disabled={sending || !input.trim()}>
              Send
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        className="bugbot-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close BugBot chat' : 'Open BugBot chat'}
      >
        {open ? <span className="bugbot-launcher-close">×</span> : <img src="/bugbot-icon-128.png" alt="" width="52" height="52" />}
      </button>
    </div>
  );
}
